import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  CheckCircle,
  AlertCircle,
  AlertTriangle,
  Shield,
  FileCheck,
  TrendingUp,
  TrendingDown
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FRAMEWORKS = [
  { id: "HIPAA", name: "HIPAA", fullName: "Health Insurance Portability and Accountability Act", color: "bg-blue-500" },
  { id: "SOC2", name: "SOC 2", fullName: "Service Organization Control 2", color: "bg-purple-500" },
  { id: "NIST", name: "NIST", fullName: "National Institute of Standards and Technology", color: "bg-green-500" },
  { id: "OWASP", name: "OWASP", fullName: "OWASP Application Security Verification Standard", color: "bg-orange-500" },
  { id: "CIS", name: "CIS", fullName: "Center for Internet Security Benchmarks", color: "bg-cyan-500" },
  { id: "HITRUST", name: "HITRUST READINESS", fullName: "Health Information Trust Alliance CSF", color: "bg-pink-500" }
];

export default function Compliance({ user, logout, token }) {
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFramework, setActiveFramework] = useState("all");

  useEffect(() => {
    fetchScans();
  }, []);

  const fetchScans = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${BACKEND_URL}/api/scans`, { headers, withCredentials: true });
      setScans(response.data.filter(s => s.status === 'completed' && s.compliance_results?.length > 0));
    } catch (error) {
      console.error('Error fetching scans:', error);
      toast.error("Failed to load compliance data");
    } finally {
      setLoading(false);
    }
  };

  // Aggregate compliance data across all scans
  const aggregateCompliance = () => {
    const frameworkStats = {};
    
    FRAMEWORKS.forEach(fw => {
      frameworkStats[fw.id] = { passed: 0, failed: 0, partial: 0, total: 0, checks: [] };
    });

    scans.forEach(scan => {
      scan.compliance_results?.forEach(check => {
        if (frameworkStats[check.framework]) {
          frameworkStats[check.framework].total++;
          frameworkStats[check.framework].checks.push({ ...check, scanName: scan.name, scanDate: scan.created_at });
          
          if (check.status === 'pass') {
            frameworkStats[check.framework].passed++;
          } else if (check.status === 'fail') {
            frameworkStats[check.framework].failed++;
          } else {
            frameworkStats[check.framework].partial++;
          }
        }
      });
    });

    return frameworkStats;
  };

  const stats = aggregateCompliance();

  const getStatusColor = (status) => {
    const colors = {
      pass: "text-emerald-500 bg-emerald-500/10",
      fail: "text-red-500 bg-red-500/10",
      partial: "text-yellow-500 bg-yellow-500/10",
      not_applicable: "text-slate-400 bg-slate-500/10"
    };
    return colors[status] || colors.not_applicable;
  };

  const getOverallScore = () => {
    let totalPassed = 0;
    let totalChecks = 0;
    
    Object.values(stats).forEach(fw => {
      totalPassed += fw.passed;
      totalChecks += fw.total;
    });

    return totalChecks > 0 ? Math.round((totalPassed / totalChecks) * 100) : 0;
  };

  if (loading) {
    return (
      <DashboardLayout user={user} logout={logout}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-primary">Loading compliance data...</div>
        </div>
      </DashboardLayout>
    );
  }

  const overallScore = getOverallScore();

  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="space-y-6" data-testid="compliance-page">
        {/* Header */}
        <div>
          <h2 className="text-2xl font-bold font-[Chivo]">Compliance Center</h2>
          <p className="text-muted-foreground">
            Healthcare compliance status across all frameworks
          </p>
        </div>

        {/* Overall Score */}
        <div className="grid md:grid-cols-3 gap-6">
          <Card className="md:col-span-1 bg-card border-border" data-testid="overall-score">
            <CardContent className="pt-6">
              <div className="text-center">
                <div className={`h-32 w-32 rounded-full mx-auto flex items-center justify-center mb-4 ${
                  overallScore >= 80 ? 'bg-emerald-500/10' :
                  overallScore >= 50 ? 'bg-yellow-500/10' : 'bg-red-500/10'
                }`}>
                  <div>
                    <p className={`text-4xl font-bold font-[Chivo] ${
                      overallScore >= 80 ? 'text-emerald-500' :
                      overallScore >= 50 ? 'text-yellow-500' : 'text-red-500'
                    }`}>
                      {overallScore}%
                    </p>
                    <p className="text-xs text-muted-foreground">Compliance</p>
                  </div>
                </div>
                <p className="font-medium">Overall Compliance Score</p>
                <p className="text-sm text-muted-foreground">Based on all scanned frameworks</p>
              </div>
            </CardContent>
          </Card>

          <Card className="md:col-span-2 bg-card border-border">
            <CardHeader>
              <CardTitle className="font-[Chivo]">Framework Overview</CardTitle>
              <CardDescription>Compliance status by framework</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {FRAMEWORKS.map((framework) => {
                  const fwStats = stats[framework.id];
                  const percentage = fwStats.total > 0 
                    ? Math.round((fwStats.passed / fwStats.total) * 100) 
                    : 0;
                  
                  return (
                    <div key={framework.id} className="space-y-2" data-testid={`framework-${framework.id}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className={`h-3 w-3 rounded-full ${framework.color}`} />
                          <span className="font-medium">{framework.name}</span>
                          {fwStats.total > 0 && (
                            <span className="text-sm text-muted-foreground">
                              ({fwStats.passed}/{fwStats.total} passed)
                            </span>
                          )}
                        </div>
                        <Badge className={
                          fwStats.total === 0 ? 'bg-slate-500/10 text-slate-400' :
                          percentage >= 80 ? 'bg-emerald-500/10 text-emerald-500' :
                          percentage >= 50 ? 'bg-yellow-500/10 text-yellow-500' :
                          'bg-red-500/10 text-red-500'
                        }>
                          {fwStats.total === 0 ? 'Not Scanned' : `${percentage}%`}
                        </Badge>
                      </div>
                      <Progress 
                        value={percentage} 
                        className="h-2" 
                      />
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Detailed Compliance */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="font-[Chivo]">Detailed Compliance Results</CardTitle>
            <CardDescription>View compliance checks by framework</CardDescription>
          </CardHeader>
          <CardContent>
            {scans.length === 0 ? (
              <div className="text-center py-12">
                <FileCheck className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">No compliance data available</h3>
                <p className="text-muted-foreground">
                  Run a scan with compliance frameworks to see results here
                </p>
              </div>
            ) : (
              <Tabs defaultValue={FRAMEWORKS[0].id} className="space-y-4">
                <TabsList className="flex flex-wrap gap-2 bg-transparent h-auto p-0">
                  {FRAMEWORKS.map((framework) => (
                    <TabsTrigger 
                      key={framework.id}
                      value={framework.id}
                      className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary"
                      data-testid={`tab-${framework.id}`}
                    >
                      {framework.name}
                      {stats[framework.id].total > 0 && (
                        <span className="ml-2 text-xs">
                          ({stats[framework.id].passed}/{stats[framework.id].total})
                        </span>
                      )}
                    </TabsTrigger>
                  ))}
                </TabsList>

                {FRAMEWORKS.map((framework) => (
                  <TabsContent key={framework.id} value={framework.id}>
                    <div className="mb-4">
                      <h3 className="text-lg font-bold font-[Chivo]">{framework.fullName}</h3>
                    </div>
                    
                    {stats[framework.id].total === 0 ? (
                      <div className="text-center py-8 border border-dashed border-border rounded-sm">
                        <Shield className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                        <p className="text-muted-foreground">
                          No {framework.name} compliance checks performed yet
                        </p>
                      </div>
                    ) : (
                      <ScrollArea className="h-[400px] pr-4">
                        <div className="space-y-2">
                          {stats[framework.id].checks.map((check, index) => (
                            <motion.div
                              key={`${check.check_id}-${index}`}
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: index * 0.02 }}
                              className="flex items-center justify-between p-4 border border-border rounded-sm"
                              data-testid={`check-${check.check_id}`}
                            >
                              <div className="flex items-center gap-3">
                                <div className={`p-2 rounded-sm ${getStatusColor(check.status)}`}>
                                  {check.status === 'pass' ? (
                                    <CheckCircle className="h-4 w-4" />
                                  ) : check.status === 'fail' ? (
                                    <AlertCircle className="h-4 w-4" />
                                  ) : (
                                    <AlertTriangle className="h-4 w-4" />
                                  )}
                                </div>
                                <div>
                                  <p className="font-medium">
                                    {check.control_id}: {check.control_name}
                                  </p>
                                  <p className="text-sm text-muted-foreground">{check.evidence}</p>
                                  <p className="text-xs text-muted-foreground mt-1">
                                    From: {check.scanName} • {new Date(check.scanDate).toLocaleDateString()}
                                  </p>
                                </div>
                              </div>
                              <div className="flex flex-col items-end gap-1">
                                <Badge className={getStatusColor(check.status)}>
                                  {check.status.replace('_', ' ').toUpperCase()}
                                </Badge>
                                {check.remediation && (
                                  <span className="text-xs text-muted-foreground max-w-xs text-right">
                                    {check.remediation}
                                  </span>
                                )}
                              </div>
                            </motion.div>
                          ))}
                        </div>
                      </ScrollArea>
                    )}
                  </TabsContent>
                ))}
              </Tabs>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
